/**
 * Implement logic to support the view layer
 */

import { convertString2JSON, groupByByKey, isExternalLink, isJSONString } from "utils";
import { KeyConstant, SystemConstant } from "const";
import { deepCloneJsonObject, removeDuplicateInArray, toCamel } from "./index";
import { getInteractor } from "services/local.service";
import store from "redux-store";

const StorageUtil = window.electronUtils.storage;

export const showConfirmLeaveGroup = async (data, setIsConfirmLeave, setIsChooseAdmin) => {
  const memberIdArray = (data.groupMembers || []).map(item => item.id);

  const adminList = await getInteractor().LocalAccountGroupService.find({
    group_id: data.id,
    type: SystemConstant.ROLE_OBJ.admin,
    account_id: memberIdArray,
  });
  const currentAccountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);
  const isAdmin = adminList.findIndex(item => item.account_id === currentAccountId) >= 0;

  if (isAdmin && adminList.length === 1) {
    setIsChooseAdmin(true);
  } else {
    setIsConfirmLeave(true);
  }
};

const NOTICE_CALL_STATUS = [SystemConstant.MESSAGE_CALL_STATUS.missed, SystemConstant.MESSAGE_CALL_STATUS.end];

export const notificationClear = async result => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);
  const mapByGroup = {};
  let clearList = [];
  let allNotSeen = {};
  result.forEach(obj => {
    if (!mapByGroup[obj.group_id]) {
      mapByGroup[obj.group_id] = [];
    }
    mapByGroup[obj.group_id].push(obj);
    if (!allNotSeen[obj.id]) {
      allNotSeen[obj.id] = obj;
    }
  });
  let groupDeleted =
    Object.keys(mapByGroup).length === 0
      ? []
      : await getInteractor().LocalGroupService.find({
          account_id: accountId,
          branch_id: branchId,
          id: Object.keys(mapByGroup),
        });
  let groupIds = groupDeleted.map(gd => gd.id);
  let groupDeletedIds = groupDeleted
    .filter(
      gd => gd.state === SystemConstant.STATE.inactive || gd.account_group_state === SystemConstant.STATE.inactive,
    )
    .map(gd => gd.id);
  let diff = Object.keys(mapByGroup);
  diff = diff.filter(d => !groupIds.includes(d));
  diff = diff.concat(groupDeletedIds);
  diff.forEach(it => {
    mapByGroup[it].forEach(mes => {
      if (mes) {
        mes.status = SystemConstant.MESSAGE_STATUS.read;
        delete allNotSeen[mes.id];
        clearList.push(mes);
      }
    });
  });

  let threadMap = groupByByKey("thread_id", Object.values(allNotSeen));
  let threadParentMessageIds = (
    await getInteractor().LocalMessageService.find({
      source_id: Object.keys(threadMap),
      send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
      state: SystemConstant.STATE.active,
    })
  ).map(it => it.source_id);
  threadParentMessageIds.forEach(tp => {
    delete threadMap[tp];
  });
  Object.keys(threadMap).forEach(tm => {
    if (threadMap[tm]) {
      threadMap[tm].forEach(mes => {
        mes.status = SystemConstant.MESSAGE_STATUS.read;
        delete allNotSeen[mes.id];
        clearList.push(mes);
      });
    }
  });
  let parentMap = groupByByKey("parent_id", Object.values(allNotSeen));
  let parentMessage = (
    await getInteractor().LocalMessageService.find({
      source_id: Object.keys(parentMap),
      send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
      state: SystemConstant.STATE.active,
    })
  ).map(it => it.source_id);
  parentMessage.forEach(tp => {
    delete parentMap[tp];
  });
  Object.keys(parentMap).forEach(tm => {
    if (parentMap[tm]) {
      parentMap[tm].forEach(mes => {
        mes.status = SystemConstant.MESSAGE_STATUS.read;
        delete allNotSeen[mes.id];
        clearList.push(mes);
      });
    }
  });
  let clone = deepCloneJsonObject(Object.values(allNotSeen));
  clone.forEach(al => {
    if (
      al.send_type === SystemConstant.SEND_TYPE.deleteMessage ||
      al.send_type === SystemConstant.SEND_TYPE.deleteReaction ||
      (SystemConstant.EDITABLE_SEND_TYPE.includes(al.send_type) && Boolean(al.parent_id)) || // edited message
      (SystemConstant.ARR_CALLING_TYPES.includes(al.send_type) && !NOTICE_CALL_STATUS.includes(al.call_status)) // notice call
    ) {
      delete allNotSeen[al.id];
      clearList.push(al);
    }
  });
  let cloneList = deepCloneJsonObject(Object.values(allNotSeen));
  for (let index = 0; index < cloneList.length; index++) {
    const notification = cloneList[index];
    try {
      const seenMemberIds = (
        await getInteractor().LocalSeenMemberService.find({ source_id: notification.source_id })
      ).map(item => item.member_account_id);

      if (Array.isArray(seenMemberIds) && seenMemberIds.includes(accountId)) {
        delete allNotSeen[notification.id];
        clearList.push(notification);
      }
    } catch (e) {
      console.error(e);
    }
  }

  clearList = clearList.filter(c => c.status !== SystemConstant.NOTIFICATION_STATUS.read);
  return { allNotSeen: allNotSeen, clearList: clearList };
};

// Return new mention arr in thread
export const validateMentions = (threadInfo, newMentions, parentMentions) => {
  const threadMentions = Boolean(threadInfo) ? JSON.parse(threadInfo.thread_mentions) : [];

  let newMentionArr = [];
  if (newMentions && Array.isArray(newMentions)) {
    newMentionArr = newMentions;
  } else if (newMentions) {
    newMentionArr = JSON.parse(newMentions);
  }
  const parentMentionArr = Boolean(parentMentions) ? JSON.parse(parentMentions) : [];
  return removeDuplicateInArray([...parentMentionArr, ...threadMentions, ...newMentionArr]);
};

export const validateInvolved = (message, parentMessage, mentionArr) => {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);

  return (
    (parentMessage && parentMessage.sender_id === accountId) ||
    message.sender_id === accountId ||
    mentionArr.includes(accountId)
  );
};

// Handling send_type if it is missing
export const getSendType = (sendType, content, isPersonal) => {
  let newSendType = sendType;

  if (false === Object.values(SystemConstant.SEND_TYPE).includes(sendType)) {
    // Set the default send_type based on the current user is chatting in a group or with an individual
    newSendType = isPersonal ? SystemConstant.SEND_TYPE.message : SystemConstant.SEND_TYPE.groupMessage;
  }

  // Update sendType if message is text and contain url
  if (
    [SystemConstant.SEND_TYPE.message, SystemConstant.SEND_TYPE.groupMessage].includes(newSendType) &&
    isExternalLink(content)
  ) {
    newSendType = SystemConstant.SEND_TYPE.link;
  }

  return newSendType;
};

export const isChangeGroupData = (group, compareGroup) => {
  const isChangeGroup = group.id && group.id === compareGroup.id;
  const isChangeGroupName = compareGroup.groupName && group.groupName !== compareGroup.groupName;
  const isChangeGroupAvar = compareGroup.avatarId && group.avatarId !== compareGroup.avatarId;
  const isChangeGroupMember = compareGroup.groupMembers && group.groupMembers !== compareGroup.groupMembers;
  return isChangeGroup || isChangeGroupName || isChangeGroupAvar || isChangeGroupMember;
};

export const isGroupOrChannelType = groupType => {
  return Boolean(
    groupType &&
      (groupType === SystemConstant.GROUP_CHAT_TYPE.group || groupType === SystemConstant.GROUP_CHAT_TYPE.channel),
  );
};

export const checkCurrentGroup = groupId =>
  groupId === store.getState().conversationRedux.selectedGroupId && !store.getState().systemRedux.isShowMnRequest;
export const checkCurrentThread = threadId =>
  threadId === store.getState().conversationRedux.threadingId && !store.getState().systemRedux.isShowMnRequest;
export const checkFocusedApp = () => Boolean(store.getState().systemRedux.isFocused);

export const getLatestMessage = async (prefixKey, chattingMessage) => {
  let camelMessage = toCamel(chattingMessage);
  const messageSourceId = camelMessage.parentId || camelMessage.sourceId;
  const originMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
    source_id: messageSourceId,
    send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
    state: SystemConstant.STATE.active,
  });

  if (camelMessage.sendType === SystemConstant.SEND_TYPE.quote && isJSONString(camelMessage.content)) {
    const contentObj = convertString2JSON(camelMessage.content, {});
    const { content_message, quote_id } = contentObj;
    camelMessage.content = content_message;
    camelMessage.quoteId = quote_id;
  }

  let isDisappear = false;
  const messageOptions = originMessage?.options ? convertString2JSON(originMessage.options, {}) : {};
  const { disappearing_f, time_to_live } = messageOptions;
  if (Boolean(disappearing_f) && Number(time_to_live) > 0) isDisappear = true;

  const lastChildMessage = (
    await getInteractor(prefixKey).LocalMessageService.getChildMessages(messageSourceId, [
      camelMessage.sendType,
      SystemConstant.SEND_TYPE.groupMessage,
      SystemConstant.SEND_TYPE.message,
      SystemConstant.SEND_TYPE.link,
      SystemConstant.SEND_TYPE.quote,
    ])
  )[0];

  let isPin = false;
  const pinDetail = await getInteractor(prefixKey).LocalGroupMessageService.findOne({
    source_message_id: camelMessage.sourceId,
    type: SystemConstant.GROUP_MESSAGE_TYPE.pin,
    state: SystemConstant.STATE.active,
  });
  if (pinDetail && pinDetail.id) isPin = true;

  const hasEditedMessage = Boolean(lastChildMessage && lastChildMessage.id);
  if (hasEditedMessage) {
    const isEditQuote = lastChildMessage.send_type === SystemConstant.SEND_TYPE.quote;
    let lastContent;
    let quoteId;
    if (isEditQuote) {
      const quoteContent = convertString2JSON(lastChildMessage.content, {});
      lastContent = quoteContent.content_message;
      quoteId = quoteContent.quote_id;
    } else {
      lastContent = lastChildMessage.content;
    }

    camelMessage = {
      ...camelMessage,
      mentions: lastChildMessage.mentions,
      content: lastContent,
      sendType: lastChildMessage.send_type,
    };

    if (quoteId) camelMessage.quoteId = quoteId;
  }

  camelMessage = {
    ...camelMessage,
    created: originMessage?.created || camelMessage.created,
    isEdited: hasEditedMessage,
    isPin,
    isDisappear,
  };
  return camelMessage;
};

export const convertScreenLockPassword = (input, passwordLength = 4) => {
  return input.replace(/\D/g, "").slice(0, passwordLength);
};

/**
 * Check created time message/ notice and confirm it need to show or not
 * @param {JsonObject} data is message/ notification object
 * @param {string} prefixKey is message/ notification object
 * @returns
 */
export const checkingPushNotification = async (data, prefixKey) => {
  if (!data || !data.id || !data.created) return false;

  const currentTime = Date.now();
  const startAppTime = StorageUtil.getCommonKey(KeyConstant.KEY_START_APP_TIME) || currentTime;
  //check for message duplicate and error
  if (data.sourceId) {
    const notErrorMessage = await getInteractor(prefixKey).LocalMessageService.getMessagesBySourceIdAndNotError(
      data.sourceId,
    );
    if (!notErrorMessage || notErrorMessage[0]?.id !== data.id) return false;
  }
  // Save time if startTime don't have value
  if (currentTime === startAppTime) StorageUtil.setCommonKey(KeyConstant.KEY_START_APP_TIME, currentTime);

  // Skip old message base on created_message_time and start_app_time
  const validTime2Push = startAppTime <= data.created ? true : startAppTime - data >= 3000;
  return validTime2Push;
};
